
import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, FolderOpen, AlertTriangle, Plus } from 'lucide-react';
import { Sector, User, UserRole } from '@/types'; // Corrected import path
import { CreateSectorModalProps } from '@/components/interfaces'; // Importado da interface centralizada

export const CreateSectorModal: React.FC<CreateSectorModalProps> = React.memo(({
  sectors,
  onClose,
  onSaveSector,
  currentUser,
  initialSectorData,
}) => {
  const [sectorName, setSectorName] = useState('');
  const [parentSectorId, setParentSectorId] = useState<string | null>(initialSectorData?.parentId ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const sectorNameInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = currentUser.role === UserRole.ADMIN;
  // Apenas admins podem criar setores de nível superior (sem pai)
  // Publicadores e admins podem criar sub-setores
  const canCreateTopLevel = isAdmin; 
  const canCreateSubSector = currentUser.role === UserRole.PUBLISHER || isAdmin;

  // Use useEffect to initialize form fields based on initialSectorData
  useEffect(() => {
    if (initialSectorData?.id) {
      // Editing existing sector
      setEditingId(initialSectorData.id);
      setSectorName(initialSectorData.name || '');
      setParentSectorId(initialSectorData.parentId ?? null);
    } else {
      // Creating new sector
      setEditingId(null);
      setSectorName(initialSectorData?.name || ''); // Pre-fill name if provided for new
      setParentSectorId(initialSectorData?.parentId ?? null); // Pre-fill parentId if provided for new
    }

    // Set focus on input when modal opens
    if (sectorNameInputRef.current) {
      sectorNameInputRef.current.focus();
    }
  }, [initialSectorData]); // Depend on initialSectorData to re-initialize when it changes

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedSectorName = sectorName.trim();

    if (!trimmedSectorName) {
      setError("O nome do setor não pode ser vazio.");
      return;
    }

    // Determine the effective parentId for validation
    const effectiveParentId = parentSectorId === '' ? null : parentSectorId;

    // Validate uniqueness: sector name must be unique among siblings (sectors with the same parentId)
    const isDuplicate = sectors.some(s =>
      s.id !== editingId && // Exclude the current sector if editing
      s.name.toLowerCase() === trimmedSectorName.toLowerCase() &&
      s.parentId === effectiveParentId
    );

    if (isDuplicate) {
      setError("Já existe um setor/pasta com este nome neste nível. Escolha um nome diferente.");
      return;
    }

    setIsSubmitting(true);
    try {
      const sectorToSave: Sector = {
        id: editingId || Date.now().toString(), // Use existing ID or generate new
        name: trimmedSectorName,
        parentId: effectiveParentId,
      };
      await onSaveSector(sectorToSave);
      alert(editingId ? "Setor atualizado com sucesso!" : "Setor criado com sucesso!");
      onClose();
    } catch (err: any) {
      console.error("Erro ao salvar setor:", err);
      setError(err.message || "Ocorreu um erro ao salvar o setor.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const topLevelSectors = sectors.filter(s => !s.parentId);
  const currentParentSector = sectors.find(s => s.id === parentSectorId);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold flex items-center gap-2 text-slate-800">
            <FolderOpen className="w-5 h-5 text-blue-600" />
            {editingId ? 'Editar Setor' : 'Criar Novo Setor'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2 border border-red-100">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nome do Setor/Pasta</label>
            <input
              ref={sectorNameInputRef}
              type="text"
              required
              value={sectorName}
              onChange={e => setSectorName(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Ex: Recursos Humanos"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Setor Pai (Opcional)</label>
            <select
              value={parentSectorId || ''}
              onChange={e => setParentSectorId(e.target.value || null)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none select-arrow"
              disabled={!canCreateTopLevel && editingId === null} // Somente admins podem criar top-level, e não pode mudar o pai ao editar se não for admin
            >
              <option value="">Nenhum (Setor Principal)</option>
              {topLevelSectors.map(s => (
                <option key={s.id} value={s.id} disabled={editingId === s.id}>
                  {s.name}
                </option>
              ))}
              {parentSectorId && !topLevelSectors.some(s => s.id === parentSectorId) && currentParentSector && (
                 <option value={currentParentSector.id} disabled>
                   {currentParentSector.name} (Sub-Setor)
                 </option>
              )}
            </select>
            {!canCreateTopLevel && editingId === null && (
                <p className="text-xs text-slate-500 mt-1">
                   Apenas administradores podem criar setores de nível superior. Você pode criar sub-pastas.
                </p>
            )}
            {parentSectorId && currentParentSector && (
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <Plus size={12} /> Será uma sub-pasta de: <strong className="text-slate-800">{currentParentSector.name}</strong>
                </p>
            )}
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {editingId ? 'Salvar Alterações' : 'Criar Setor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});